import { useEffect, useRef, useState } from 'react'
import { DataModelSchemaDetail } from 'common/types/kraken-core/DataModelSchema'
import { Breadcrumbs, Button, Layout, LoadingDots, Tabs } from '@loadsmart/loadsmart-ui'
import { Paper, Box, Badge } from '@mui/material'
import { useTabTitle } from 'hooks/useTabTitle/useTabTitle'
import useTopNavigationContext from 'hooks/useTopNavigationContext/useTopNavigationContext'
import DataModelSchemaValidation from '../components/DataModelSchemaValidation'
import DataModelSchemaStructure from '../components/DataModelSchemaStructure'
import DataModelSchemaResources from '../components/DataModelSchemaResources'
import {
  useCreateDataModelSchema,
  useGetDataModelSchema,
  useUpdateDataModelSchema,
  useListDataModelSchemaResources,
} from '../api'
import { getValidResourcesCount } from '../utils/resourceFilters'
import DataModelSchemaForm from '../components/DataModelSchemaForm'
import { useHistory, useParams } from 'react-router'
import { toast } from 'react-toastify'
import isEmpty from 'lodash.isempty'
import isEqual from 'lodash.isequal'
import { safeBase64Encode } from 'common/helpers/encoding'

export enum DataModelSchemaDetailMode {
  CREATE = 'create',
  UPDATE = 'update',
}

export interface DataModelSchemaDetailsProps {
  id?: string
  mode: DataModelSchemaDetailMode
}

export default function DataModelSchemaDetails(props: DataModelSchemaDetailsProps) {
  const changeTabTitle = useTabTitle('')
  const history = useHistory()
  const { id } = useParams<DataModelSchemaDetailsProps>()

  const [mode, setMode] = useState<DataModelSchemaDetailMode>(props.mode)
  const [model, setModel] = useState<DataModelSchemaDetail>()
  const [originalModel, setOriginalModel] = useState<DataModelSchemaDetail>()
  const [isDirty, setIsDirty] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const { data, isLoading } = useGetDataModelSchema(id || (props.id as any), {
    enabled: Boolean(id) && mode === DataModelSchemaDetailMode.UPDATE,
  })
  const {
    mutate: update,
    isSuccess: didSuccessToUpdate,
    isError: didFailtToUpdate,
    error: updateError,
    isLoading: isUpdating,
  } = useUpdateDataModelSchema()
  const {
    mutate: create,
    isSuccess: didSuccessToCreate,
    isError: didFailtToCreate,
    error: createError,
    data: createResponse,
    isLoading: isCreating,
  } = useCreateDataModelSchema()

  // Get resources data for badge count
  const { data: resourcesData, refetch: refetchResources } = useListDataModelSchemaResources({
    model_id: id || (props.id as any),
  })

  // Calculate filtered resources count (excluding INTERNAL kind)
  const filteredResourcesCount = resourcesData?.results
    ? getValidResourcesCount(resourcesData.results)
    : 0

  // Refetch resources when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      refetchResources()
    }
  }, [refreshTrigger, refetchResources])

  const topNavigation = useTopNavigationContext()
  const handleSave = useRef<Function>(() => {})
  const hasShownSuccessMessage = useRef(false)

  // Function to trigger refresh in both tabs
  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1)
  }

  const handleOnChange = (changed: DataModelSchemaDetail) => {
    setModel(changed)
  }

  const handleOnStructure = (value: string) => {
    if (model)
      setModel(c => {
        if (c) {
          return {
            ...c,
            structure: value,
          }
        }
      })
  }

  // Check if model is dirty by comparing with original
  useEffect(() => {
    if (model && originalModel) {
      const dirty = !isEqual(model, originalModel)
      setIsDirty(dirty)
    } else if (model && mode === DataModelSchemaDetailMode.CREATE) {
      // For create mode, consider dirty if model has any meaningful data
      const hasData = Boolean(model.description || model.engine || model.structure || model.owner)
      setIsDirty(hasData)
    } else {
      setIsDirty(false)
    }
  }, [model, originalModel, mode])

  handleSave.current = async () => {
    if (!model) {
      toast.error('Failure when saving')
      return
    }

    if (isEmpty(model)) {
      toast.error('Provide the required fields')
      return
    }

    // For creation, structure is not mandatory. For updates, all fields including structure are mandatory
    const mandatoryFields = model.id
      ? ['engine', 'structure', 'description']
      : ['engine', 'description']
    const missingFields = mandatoryFields.filter((f: any) => isEmpty((model as any)[f]))
    if (missingFields.length > 0) {
      toast.error(`Field(s) ${missingFields.join(' & ')} are mandatory`)
      return
    }

    // Reset success message flag when starting a new save operation
    hasShownSuccessMessage.current = false

    switch (mode) {
      case DataModelSchemaDetailMode.CREATE:
        model.structure = model.structure || safeBase64Encode('{}')
        create(model)
        break
      case DataModelSchemaDetailMode.UPDATE:
        update(model)
        break
      default:
        toast.error(`There no action for the current mode: ${mode}`)
        break
    }
  }

  useEffect(() => {
    setModel(data)
    setOriginalModel(data)
  }, [data])

  useEffect(() => {
    if (model)
      topNavigation.updateState({
        children: (
          <Layout.Group align="center" className="flex-1">
            <Layout.Box padding="none" className="flex-1">
              <Breadcrumbs
                entries={[
                  {
                    label: 'Data Models',
                    active: true,
                    url: '#',
                    onClick: () => {
                      history.push('/data-model/schemas')
                    },
                  },
                  {
                    label: `${model.description ?? ''}`,
                    active: true,
                  },
                ]}
              />
            </Layout.Box>
            <Button
              variant="primary"
              disabled={
                !isDirty ||
                (mode === DataModelSchemaDetailMode.UPDATE && isUpdating) ||
                (mode === DataModelSchemaDetailMode.CREATE && isCreating)
              }
              onClick={() => handleSave.current()}
            >
              Save
            </Button>
          </Layout.Group>
        ),
      })
  }, [model, isDirty, mode, isUpdating, isCreating])

  useEffect(() => {
    if (mode === DataModelSchemaDetailMode.CREATE) {
      setModel({})
      setOriginalModel({})
    }
    changeTabTitle(`Data Model - ${mode}`)
  }, [mode])

  useEffect(() => {
    if (didFailtToCreate || didFailtToUpdate) {
      toast.error('Something fail when creating')
      return
    }

    if ((didSuccessToCreate || didSuccessToUpdate) && !hasShownSuccessMessage.current) {
      toast.success('Successfully saved')
      hasShownSuccessMessage.current = true

      if (didSuccessToCreate) {
        setMode(DataModelSchemaDetailMode.UPDATE)
        setModel(createResponse)
        setOriginalModel(createResponse)
        setIsDirty(false)
        // Navigate to the new URL with the generated ID
        if (createResponse?.id) {
          history.replace(`/data-model/schemas/${createResponse.id}`)
        }
      } else if (didSuccessToUpdate) {
        // Update original model after successful update to reset dirty state
        setOriginalModel(model)
        setIsDirty(false)
      }
    }
  }, [didSuccessToCreate, didFailtToCreate, didFailtToUpdate, didSuccessToUpdate])

  return (
    <Paper elevation={1} sx={{ p: 2, minHeight: '90vh' }}>
      <Box>
        {isLoading || !model ? <LoadingDots /> : null}

        {!isLoading && model ? (
          <Layout.Stack space="m">
            <DataModelSchemaForm object={model} onChange={handleOnChange} />
            {model.id ? (
              <Tabs>
                <Tabs.Items>
                  <Tabs.Item name="schema" default>
                    Schema
                  </Tabs.Item>

                  {/* Only availble when there's no pending changes */}
                  <Tabs.Item name="validation">Validation</Tabs.Item>

                  {/* Resources tab - only available when model exists */}
                  <Tabs.Item name="resources">
                    <Badge
                      badgeContent={filteredResourcesCount}
                      color="primary"
                      invisible={filteredResourcesCount === 0}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.625rem',
                          height: '16px',
                          minWidth: '16px',
                          padding: '0 4px',
                          top: '2px',
                          right: '-8px',
                        },
                      }}
                    >
                      Resources
                    </Badge>
                  </Tabs.Item>
                </Tabs.Items>
                <Tabs.Panels>
                  <Tabs.Panel name="schema">
                    <DataModelSchemaStructure
                      contentType={model.engine || 'application/json'}
                      value={model.structure}
                      onChange={handleOnStructure}
                      modelId={model.id}
                    />
                  </Tabs.Panel>
                  <Tabs.Panel name="validation">
                    <DataModelSchemaValidation
                      dataModelSchemaId={model.id}
                      value={model.structure}
                      onChange={handleOnStructure}
                      refreshTrigger={refreshTrigger}
                      onResourceChange={triggerRefresh}
                    />
                  </Tabs.Panel>
                  <Tabs.Panel name="resources">
                    <DataModelSchemaResources
                      dataModelSchemaId={model.id}
                      refreshTrigger={refreshTrigger}
                      onResourceChange={triggerRefresh}
                    />
                  </Tabs.Panel>
                </Tabs.Panels>
              </Tabs>
            ) : null}
          </Layout.Stack>
        ) : null}
      </Box>
    </Paper>
  )
}
